import { Component, inject, Inject, OnInit } from '@angular/core';
import { IonApp, IonButton, IonIcon, IonSpinner } from '@ionic/angular/standalone';
import { LeftSideBarComponent } from '@components/left-side-bar/left-side-bar.component';
import { NavbarComponent } from '@components/navbar/navbar.component';
import { ApiService, LOADING_STATUS } from './services/api.service';
import { RouteGuardService } from './services/route-guard.service';
import { Store } from '@ngrx/store';
import {
  setAzureAccessToken,
  setFilters,
  prefFilters,
  setHardFilters,
  setProfile,
  setVoidFilters,
  setScreenAccess,
  setRecsBatchDate,
  setVoidBatchDate,
} from './state/actions/app.action';
import { Filter, SELECTED_FILTER_VALUES } from './types/filter.types';
import { CommonModule } from '@angular/common';
import { catchError, filter, forkJoin, lastValueFrom, Observable, of, Subject, takeUntil } from 'rxjs';
import { Router, RouterOutlet } from '@angular/router';
import { ToasterService } from './services/toaster.service';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { EventType, InteractionStatus, RedirectRequest } from '@azure/msal-browser';
import { AppState } from './types/app.types';
import { addIcons } from 'ionicons';
import * as allIcons from 'ionicons/icons';
import { switchMap, tap } from 'rxjs/operators';
import { roleMapping } from './constants/pivot.constant';
import { RouteAccessService } from './services/route-access.service';
import { GenaiService } from './services/genai-service.service';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.css'],
  standalone: true,
  styles: [
    `
      :host {
        all: initial;
      }
    `,
  ],
  imports: [CommonModule, IonApp, IonSpinner, IonIcon, IonButton, RouterOutlet, NavbarComponent, LeftSideBarComponent],
})
export class AppComponent implements OnInit {
  private authService = inject(MsalService);
  appState$: Observable<string | null>;
  appLoadingStatus: LOADING_STATUS = LOADING_STATUS.NOT_STARTED;
  hiddenMain: boolean = true;
  fadeInMain: boolean = false;
  isIframe = false;
  loginDisplay = false;
  screenAccess: string[] = [];

  private readonly _destroying$ = new Subject<void>();

  hiddenAppLoader: boolean = false;
  fadeOutAppLoader: boolean = false;

  dynamicStyles: string = ``;
  hasApiFailed: boolean = false;
  errorData: any;
  constructor(
    private apiService: ApiService,
    private routeGuardService: RouteGuardService,
    private store: Store, // private modalController: ModalController
    private toastService: ToasterService,
    private msalBroadcastService: MsalBroadcastService,
    private router: Router,
    private routeAccessService: RouteAccessService,
    private genaiService: GenaiService,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
  ) {
    addIcons(allIcons);
    this.appState$ = this.store.select((state: any) => {
      return state.app;
    });
  }

  async ngOnInit() {
    this.msalInit();
    this.appState$?.subscribe((appState: any) => {
      if (appState.azureAccessToken && appState.azureIdToken) {
        setTimeout(() => {
          this.initializeAppData(appState.azureAccessToken, appState.azureIdToken);
        }, 1000);
      }
    });
  }

  msalInit() {
    this.authService.handleRedirectObservable().subscribe();
    this.isIframe = window !== window.parent && !window.opener; // Remove this line to use Angular Universal
    this.setLoginDisplay();

    this.authService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window
    this.msalBroadcastService.msalSubject$.subscribe((result: any) => {
      if (result.eventType === EventType.ACCOUNT_ADDED || result.eventType === EventType.ACCOUNT_REMOVED) {
        if (this.authService.instance.getAllAccounts().length === 0) {
          window.location.pathname = '/';
        } else {
          this.setLoginDisplay();
        }
      } else if (result.eventType === EventType.ACQUIRE_TOKEN_SUCCESS || result.eventType === EventType.LOGIN_SUCCESS) {
        // Siddhi
        // console.log(
        //   'result.payload in app.component.ts bottlerID ',
        //   result.payload.idTokenClaims.extension_6bc6a581cda24312aee39ca7a533365f_BOTTLERID,
        // );
        // console.log(
        //   'result.payload in app.component.ts roles should be NextBestSKU.Admin  ',
        //   result.payload.idTokenClaims.roles,
        // );
        sessionStorage.setItem('accessToken', result.payload?.accessToken);
        sessionStorage.setItem('idToken', result.payload?.idToken);
        // sessionStorage.setItem('role', roleMapping[result.payload.idTokenClaims.roles[0]]?.[0]);
        sessionStorage.setItem('role', this.rolePriority(result.payload.idTokenClaims.roles));
        // testing purpose
        // sessionStorage.setItem('role', 'FSOP');
        sessionStorage.setItem(
          'bottlerID',
          result.payload.idTokenClaims.extension_6bc6a581cda24312aee39ca7a533365f_COMPANY,
        );
        // testing purpose
        // sessionStorage.setItem('bottlerID', '4100');
        this.store.dispatch(
          setAzureAccessToken({ accessToken: result.payload?.accessToken, idToken: result.payload?.idToken }),
        );
      } else if (result.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
        // this.loginRedirect();
      }
    });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$),
      )
      .subscribe((result) => {
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();
      });
  }

  checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    let activeAccount = this.authService.instance.getActiveAccount();

    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      let accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
    }
  }

  setLoginDisplay() {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
  }

  async initializeAppData(accessToken: string, azureIdToken: string): Promise<void> {
    this.hasApiFailed = false;

    if ([LOADING_STATUS.NOT_STARTED, LOADING_STATUS.ERROR].includes(this.appLoadingStatus)) {
      this.appLoadingStatus = LOADING_STATUS.LOADING;

      try {
        const screenAccessResponse = await lastValueFrom(
          this.apiService.getScreenAccess().pipe(
            catchError((err) => {
              console.error('Get Screen Access API failed after login', err);
              // Return a default structure so subsequent code doesn't break
              return of({ data: { screen_access: [] } });
            }),
            // Use tap to perform side effects after the value is received
            tap((response) => {
              this.screenAccess = response.data.screen_access;
              this.store.dispatch(setScreenAccess({ screenAccessState: this.screenAccess }));
              console.log('Screen access updated after LOGIN_SUCCESS:', this.screenAccess);
              this.routeAccessService.redirectToDefaultPage(this.screenAccess);
            }),
          ),
        );

        const currentScreenAccess = screenAccessResponse.data.screen_access;

        const apiCalls: Observable<any>[] = [];

        apiCalls.push(
          this.apiService.getAzureProfile().pipe(
            catchError((err) => {
              console.error('Azure Profile API failed', err);
              return of(null);
            }),
          ),
        );

        apiCalls.push(
          this.apiService.getUserAvatarImage().pipe(
            catchError((err) => {
              console.error('Avatar API failed', err);
              return of(null);
            }),
          ),
        );

        if (currentScreenAccess && currentScreenAccess.includes('RECS')) {
          apiCalls.push(
            this.apiService.getUserPreference().pipe(
              catchError((err) => {
                console.error('User Preference API failed', err);
                return of({ data: [] });
              }),
            ),
          );

          apiCalls.push(
            this.apiService.getFilterValues().pipe(
              catchError((err) => {
                console.error('Filter Values API failed', err);
                return of({ data: [] });
              }),
            ),
          );
        } else {
          // If RECS is not included, push an observable that immediately completes with null
          // to maintain the correct index in the forkJoin result array.
          apiCalls.push(of(null));//for user preference
          apiCalls.push(of(null));//for recs-filter values
        }

        if (currentScreenAccess && currentScreenAccess.includes('VOIDS')) {
          apiCalls.push(
            this.apiService.getVoidFiltersValues().pipe(
              catchError((err) => {
                console.error('Void Filter API failed', err);
                return of({ data: [] });
              }),
            ),
          );
        } else {
          // If VOIDS is not included, push an observable that immediately completes with null
          apiCalls.push(of(null));
        }

        apiCalls.push(
          this.apiService.getGenAiAllowedBottlersList().pipe(
            catchError((err) => {
              console.error('GenAI Bottlers API failed', err);
              return of({ data: { genAiAllowedBottlers: [] } }); 
            }),
          ),
        );

        const [profileRes, avatarImageRes, userPrefRes, filterRes, voidFilterRes, genAiBottlersRes] = await lastValueFrom(
          forkJoin(apiCalls),
        );

        const avatarBase64 = avatarImageRes
          ? 'data:image/jpeg;base64, ' + btoa(String.fromCharCode(...new Uint8Array(avatarImageRes)))
          : null;

        this.store.dispatch(
          setProfile({
            profile: {
              fullName: (profileRes as any)?.displayName ?? '',
              emailAddress: (profileRes as any)?.mail ?? '',
              avatar: avatarBase64,
              role: sessionStorage.getItem('role'),
              bottlerId: sessionStorage.getItem('bottlerID'),
            },
          }),
        );

        if (currentScreenAccess && currentScreenAccess.includes('RECS') && filterRes) {
          try {
            this.storePreference(userPrefRes.data);
          } catch (error) {
            this.storePreference([]);
          }
          try {
            this.storeFilters(filterRes.data);
          } catch (error) {
            this.storeFilters({ finalFilter: [], batchNo: null }); // Pass expected structure
          }
          try {
            this.storePrefFilters(filterRes.data);
          } catch (error) {
            this.storePrefFilters({ finalFilter: [], batchNo: null }); // Pass expected structure
          }
        }

        if (currentScreenAccess && currentScreenAccess.includes('VOIDS') && voidFilterRes) {
          try {
            this.storeVoidFilters(voidFilterRes.data);
          } catch (error) {
            this.storeVoidFilters({ finalFilter: [], batchDate: null }); // Pass expected structure
          }
        }

        if (genAiBottlersRes && genAiBottlersRes.data) {
          this.genaiService.setAllowedBottlers(genAiBottlersRes.data.genAiAllowedBottlers);
        }

        this.appLoadingStatus = LOADING_STATUS.LOADED;
        this.showMain();
      } catch (error) {
        console.error('Initialization failed', error);
        this.hasApiFailed = true;
      } finally {
        this.appLoadingStatus = LOADING_STATUS.LOADED;
      }
    }
  }

  retryApiCalls(): void {
    this.appLoadingStatus = LOADING_STATUS.ERROR;
    this.appState$?.subscribe((appState: any) => {
      if (appState.azureAccessToken && appState.azureIdToken) {
        this.initializeAppData(appState.azureAccessToken, appState.azureIdToken);
      }
    });
  }

  getIsAppLoaded() {
    return this.appLoadingStatus === LOADING_STATUS.LOADED;
  }

  getMainClasses() {
    return {
      fadeIn: this.fadeInMain,
      hidden: this.hiddenMain,
    };
  }

  getAppLoaderClasses() {
    return {
      hidden: this.hiddenAppLoader,
      fadeOut: this.fadeOutAppLoader,
    };
  }

  showMain() {
    setTimeout(() => {
      this.fadeOutAppLoader = true;
    }, 1000);
    setTimeout(() => {
      this.hiddenAppLoader = true;
      this.hiddenMain = false;
      this.fadeInMain = true;
    }, 1800);
  }

  storePreference(data: any) {
    const bottlerRole = data?.userDetail?.bottlerRole;
    if (bottlerRole) {
      sessionStorage.setItem('bottlerRole', bottlerRole);
    }

    const currentBottlerId = data?.userDetail?.bottlerId;
    if (currentBottlerId) {
      sessionStorage.setItem('currentBottlerId', currentBottlerId);
    }

    if (data?.preferences) {
      const hardFilters: SELECTED_FILTER_VALUES[] = data?.preferences.hardFilters.map((preference: any) => {
        let filterKey = preference.filterKey;
        return { filterKey, values: preference.values };
      });
      this.store.dispatch(setHardFilters({ hardFilters }));
    }
  }

  storePrefFilters(data: any) {
    this.showMain();
    const filters: Filter[] = data.finalFilter.map((filter: any) => {
      const copyOfFilter = { ...filter };
      copyOfFilter.default_options = copyOfFilter.default_options
        .map((option: any) => {
          const copyOfOption = { ...option }; // Make a shallow copy of the option

          if (Array.isArray(copyOfOption.metadata) && copyOfOption.metadata[0]) {
            const output = Object.entries(copyOfOption.metadata[0]).reduce((acc, [key, values]) => {
              (values as string[]).forEach((value) => acc.push(`${key}_${value}`));
              return acc;
            }, [] as string[]);
            copyOfOption.metadata = output;
          }

          return copyOfOption;
        })
        .sort((a: any, b: any) => {
          return a.label.localeCompare(b.label);
        });

      return copyOfFilter;
    });
    this.store.dispatch(setRecsBatchDate({ recsBatchDate: data.batchNo }));
    this.store.dispatch(prefFilters({ prefFilters: filters }));
    // this.appLoadingStatus = LOADING_STATUS.LOADED;
  }

  //stores data exactly how it receives from api
  storeFilters(data: any) {
    this.showMain();
    const filters: Filter[] = data.finalFilter.map((filter: any) => {
      const copyOfFilter = { ...filter };
      return copyOfFilter;
    });
    this.store.dispatch(setRecsBatchDate({ recsBatchDate: data.batchNo }));
    this.store.dispatch(setFilters({ filters }));
    // this.appLoadingStatus = LOADING_STATUS.LOADED;
  }

  storeVoidFilters(data: any) {
    this.showMain();
    const filters: Filter[] = data.finalFilter.map((filter: any) => {
      const copyOfFilter = { ...filter };
      copyOfFilter.default_options = copyOfFilter.default_options
        .map((option: any) => {
          if (Array.isArray(option.metadata) && option.metadata[0]) {
            const output = Object.entries(option.metadata[0]).reduce((acc, [key, values]) => {
              (values as string[]).forEach((value) => acc.push(`${key}_${value}`));
              return acc;
            }, [] as string[]);
            option.metadata = output;
          }

          return option;
        })
        .sort((a: any, b: any) => {
          return a.label.localeCompare(b.label);
        });

      return copyOfFilter;
    });
    this.store.dispatch(setVoidBatchDate({ voidBatchDate: data.batchDate }));
    this.store.dispatch(setVoidFilters({ filters }));
    // this.appLoadingStatus = LOADING_STATUS.LOADED;
  }
  
  rolePriority(roles: any): string {
    const rolePriority = ['NextBestSKU.Admin', 'NextBestSKU.FSOPUser', 'NextBestSKU.CRUser'];
    for (const role of rolePriority) {
      if (roles.includes(role)) return roleMapping[role][0];
    }
    return '';
  }
}
